import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import {
  RichTextEditor,
  RichTextDisplay,
  stripHtml,
} from "../shared/rich-text-editor.component.tsx";
import {
  clinicalCommentsForPatient,
  type ClinicalComment,
} from "./clinical-snippets.ts";

const TYPE_CONFIG: Record<
  ClinicalComment["type"],
  {
    label: string;
    bg: string;
    border: string;
    dot: string;
    icon: React.ReactNode;
  }
> = {
  note: {
    label: "Note",
    bg: "bg-blue-50",
    border: "border-blue-100",
    dot: "bg-blue-400",
    icon: <I.FileText size={12} />,
  },
  observation: {
    label: "Observation",
    bg: "bg-amber-50",
    border: "border-amber-100",
    dot: "bg-amber-400",
    icon: <I.AlertCircle size={12} />,
  },
  alert: {
    label: "Alerte",
    bg: "bg-red-50",
    border: "border-red-200",
    dot: "bg-red-500",
    icon: <I.AlertTriangle size={12} />,
  },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isHtml(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

function commentPreview(content: string): string {
  return isHtml(content) ? stripHtml(content) : content;
}

export function ClinicalCommentsWidget() {
  const { current } = Interactions.usePatient();
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const toast = Interactions.useToast();

  if (!current) {
    return (
      <div className="flex h-full min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-sf-border py-8 text-center">
        <I.FileText size={20} className="mb-2 text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  const comments = clinicalCommentsForPatient(current.uuid);
  const alerts = comments.filter((c) => c.type === "alert");

  return (
    <div className="flex flex-col gap-2">
      {/* Alertes en haut */}
      {alerts.map((c) => {
        const cfg = TYPE_CONFIG[c.type];
        return (
          <div
            key={c.id}
            className={cn(
              "flex items-start gap-2.5 rounded-lg border px-3 py-2.5",
              cfg.bg,
              cfg.border,
            )}
          >
            <span className="mt-0.5 shrink-0 text-red-500">{cfg.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold leading-snug text-red-700">
                {commentPreview(c.content)}
              </div>
              <div className="mt-1 text-[10.5px] text-red-400">
                {formatDate(c.date)} · {c.author}
              </div>
            </div>
          </div>
        );
      })}

      {/* Autres commentaires */}
      {comments
        .filter((c) => c.type !== "alert")
        .map((c) => {
          const cfg = TYPE_CONFIG[c.type];
          const isOpen = expanded === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setExpanded(isOpen ? null : c.id)}
              className={cn(
                "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
                cfg.border,
                isOpen ? cfg.bg : "bg-white hover:bg-gray-50",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn("h-1.5 w-1.5 shrink-0 rounded-full", cfg.dot)}
                  />
                  <span
                    className={cn(
                      "rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide",
                      c.type === "observation"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-100 text-blue-700",
                    )}
                  >
                    {cfg.label}
                  </span>
                  <span className="text-[11px] text-sf-text-muted">
                    {formatDate(c.date)}
                  </span>
                </div>
                <I.ChevronDown
                  size={13}
                  className={cn(
                    "shrink-0 text-sf-text-muted transition-transform duration-150",
                    isOpen && "rotate-180",
                  )}
                />
              </div>
              {isOpen ? (
                <RichTextDisplay
                  html={isHtml(c.content) ? c.content : `<p>${c.content}</p>`}
                  className="mt-1.5"
                />
              ) : (
                <p className="mt-1.5 line-clamp-2 text-[12.5px] leading-snug text-sf-text">
                  {commentPreview(c.content)}
                </p>
              )}
              {isOpen && (
                <p className="mt-1 text-[11px] text-sf-text-muted">
                  {c.author}
                </p>
              )}
            </button>
          );
        })}

      {/* Ajouter une note */}
      {adding ? (
        <RichTextEditor
          placeholder="Saisir une note clinique…"
          onSave={(html) => {
            const preview = stripHtml(html);
            toast.push({
              type: "success",
              title: "Note ajoutée",
              message: preview
                ? `${preview.slice(0, 60)}…`
                : "Note enregistrée",
            });
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-sf-border py-2 text-[12px] font-medium text-sf-text-muted transition-colors hover:border-sf-primary hover:text-sf-primary"
        >
          <I.Plus size={13} />
          Ajouter une note
        </button>
      )}
    </div>
  );
}
